import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import "@/App.css";

import Header from "@/components/proof/Header";
import Footer from "@/components/proof/Footer";
import ScrollRuler from "@/components/proof/ScrollRuler";
import ScrollToTop from "@/components/proof/ScrollToTop";
import MobileDrawer from "@/components/proof/MobileDrawer";
import MobileBottomBar from "@/components/proof/MobileBottomBar";
import { CartProvider } from "@/context/CartContext";
import { DrawerProvider } from "@/context/DrawerContext";
import { easing } from "@/lib/animations";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Product from "@/pages/Product";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import About from "@/pages/About";
import Account from "@/pages/Account";
import Contact from "@/pages/Contact";
import Policy from "@/pages/Policy";
import Drop from "@/pages/Drop";
import Journal from "@/pages/Journal";
import JournalPost from "@/pages/JournalPost";
import Archive from "@/pages/Archive";
import Press from "@/pages/Press";
import Shipping from "@/pages/Shipping";
import NotFound from "@/pages/NotFound";

/**
 * Subpage transition wrapper — soft fade-in for any non-home page.
 * The home page handles its own choreography via the LoadingScreen.
 */
const PageTransition = ({ children }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.55, ease: easing.primary }}
    >
        {children}
    </motion.div>
);

const AnimatedRoutes = () => {
    const location = useLocation();
    return (
        <LayoutGroup>
            <AnimatePresence mode="popLayout" initial={false}>
                <Routes location={location} key={location.pathname}>
                <Route path="/" element={<Home />} />
                <Route
                    path="/shop"
                    element={
                        <PageTransition>
                            <Shop />
                        </PageTransition>
                    }
                />
                <Route
                    path="/product/:slug"
                    element={
                        <PageTransition>
                            <Product />
                        </PageTransition>
                    }
                />
                <Route
                    path="/cart"
                    element={
                        <PageTransition>
                            <Cart />
                        </PageTransition>
                    }
                />
                <Route
                    path="/checkout"
                    element={
                        <PageTransition>
                            <Checkout />
                        </PageTransition>
                    }
                />
                <Route
                    path="/about"
                    element={
                        <PageTransition>
                            <About />
                        </PageTransition>
                    }
                />
                <Route
                    path="/account"
                    element={
                        <PageTransition>
                            <Account />
                        </PageTransition>
                    }
                />
                <Route
                    path="/contact"
                    element={
                        <PageTransition>
                            <Contact />
                        </PageTransition>
                    }
                />
                <Route
                    path="/policy/:slug"
                    element={
                        <PageTransition>
                            <Policy />
                        </PageTransition>
                    }
                />
                <Route
                    path="/drop/:slug"
                    element={
                        <PageTransition>
                            <Drop />
                        </PageTransition>
                    }
                />
                <Route
                    path="/journal"
                    element={
                        <PageTransition>
                            <Journal />
                        </PageTransition>
                    }
                />
                <Route
                    path="/journal/:slug"
                    element={
                        <PageTransition>
                            <JournalPost />
                        </PageTransition>
                    }
                />
                <Route
                    path="/archive"
                    element={
                        <PageTransition>
                            <Archive />
                        </PageTransition>
                    }
                />
                <Route
                    path="/press"
                    element={
                        <PageTransition>
                            <Press />
                        </PageTransition>
                    }
                />
                <Route
                    path="/shipping"
                    element={
                        <PageTransition>
                            <Shipping />
                        </PageTransition>
                    }
                />
                <Route
                    path="*"
                    element={
                        <PageTransition>
                            <NotFound />
                        </PageTransition>
                    }
                />
            </Routes>
            </AnimatePresence>
        </LayoutGroup>
    );
};

function App() {
    // Remove the platform "Made with Emergent" badge for the PROOF brand presentation
    useEffect(() => {
        const removeBadge = () => {
            const b = document.getElementById("emergent-badge");
            if (b) b.remove();
        };
        removeBadge();
        const interval = setInterval(removeBadge, 500);
        const stop = setTimeout(() => clearInterval(interval), 5000);
        return () => {
            clearInterval(interval);
            clearTimeout(stop);
        };
    }, []);

    return (
        <BrowserRouter>
            <CartProvider>
                <DrawerProvider>
                    <ScrollToTop />
                    <div className="App" data-testid="app-root">
                        <Header ready={true} />
                        <ScrollRuler />
                        <AnimatedRoutes />
                        <Footer />
                        {/* MobileDrawer must render OUTSIDE Header so it escapes
                            Header's mix-blend-mode stacking context. */}
                        <MobileDrawer />
                        {/* Mobile-only sticky bottom navigation bar (rotating P,
                            cart, account, search). Lives at App-root for the same
                            stacking-context reason. */}
                        <MobileBottomBar />
                    </div>
                </DrawerProvider>
            </CartProvider>
        </BrowserRouter>
    );
}

export default App;
